import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Clapperboard, Sparkles, Coins, LogOut, Shield } from "lucide-react";

export function Navbar() {
  const { user, subscription, isPaid, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const links = [
    { to: "/campaigns", label: "Discover" },
    { to: "/my-campaigns", label: "My Campaigns" },
    { to: "/workspace", label: "AI Clipping" },
    { to: "/clips", label: "My Clips" },
    { to: "/pricing", label: "Pricing" },
  ];

  const active = (to) => location.pathname === to || location.pathname.startsWith(to + "/");

  return (
    <nav className="glass sticky top-0 z-50 px-4 sm:px-8 py-3 flex items-center justify-between">
      <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2 group" data-testid="nav-logo">
        <div className="w-9 h-9 rounded-xl bg-amber-500 flex items-center justify-center amber-glow group-hover:scale-105 transition-transform">
          <Clapperboard className="w-5 h-5 text-zinc-950" strokeWidth={2.5} />
        </div>
        <span className="font-display font-extrabold text-xl tracking-tight">Clipstar</span>
      </Link>

      {user && (
        <div className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                active(l.to) ? "text-amber-400 bg-amber-500/10" : "text-zinc-400 hover:text-white hover:bg-white/5"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </div>
      )}

      <div className="flex items-center gap-3">
        {user ? (
          <>
            {isPaid ? (
              <div
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/30"
                data-testid="credits-pill"
              >
                <Coins className="w-4 h-4 text-amber-400" />
                <span className="text-sm font-semibold text-amber-300">{subscription?.credits ?? 0}</span>
                <span className="text-xs text-amber-500/70">credits</span>
              </div>
            ) : (
              <Button
                onClick={() => navigate("/pricing")}
                data-testid="unlock-clipping-nav-btn"
                className="hidden sm:flex bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold rounded-full h-9"
              >
                <Sparkles className="w-4 h-4 mr-1.5" /> Unlock AI Clipping
              </Button>
            )}
            {user.role === "admin" && (
              <Link to="/admin" data-testid="nav-admin" className="text-zinc-400 hover:text-violet-400">
                <Shield className="w-5 h-5" />
              </Link>
            )}
            <button
              onClick={() => { logout(); navigate("/"); }}
              data-testid="logout-btn"
              className="text-zinc-400 hover:text-white p-2 rounded-lg hover:bg-white/5"
              title="Log out"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </>
        ) : (
          <>
            <Link to="/login" data-testid="nav-login" className="text-sm text-zinc-300 hover:text-white px-3 py-2">
              Log in
            </Link>
            <Button
              onClick={() => navigate("/register")}
              data-testid="nav-signup"
              className="bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold rounded-full h-9"
            >
              Get Started
            </Button>
          </>
        )}
      </div>
    </nav>
  );
}
